import type { VisibilityConfig, VisibilityAudience } from '../../types/cultivar';

// Controlled per-audience visibility editor. Used by both:
//   - CultivarOwnerTabs.VisibilityTab (per-cultivar override)
//   - ProgramForm                      (per-program default)
//
// Owner-side rule: this UI only ADDS fields/attribute categories on top of
// the system defaults. The minimum (name, trade_name, crop_type, program)
// is enforced server-side and cannot be removed here.

const AUDIENCES: { key: VisibilityAudience; label: string; hint: string }[] = [
  { key: 'nursery_licensee',    label: 'Nursery licensees',    hint: 'Orgs propagating this cultivar' },
  { key: 'production_licensee', label: 'Production licensees', hint: 'Orchards growing this cultivar' },
  { key: 'evaluation_licensee', label: 'Evaluators',           hint: 'Orgs with an evaluation licence or evaluator grant' },
  { key: 'program_orchard',     label: 'Program orchards',     hint: 'Orchards licensed elsewhere in the same program' },
  { key: 'public',              label: 'Listing only',         hint: 'Orgs that see it for another reason: a licence not yet signed, or trees they grow' },
];

const FIELD_OPTIONS = [
  'botanical_name', 'species', 'cultivar_role',
  'breeder_org_id', 'breeder_org_name', 'breeder_name',
  'year_bred', 'description',
  'protection_status', 'origin_country',
];

const ATTRIBUTE_CATEGORIES = ['fruit_quality', 'tree_habit', 'season', 'disease', 'issues'];

interface Props {
  value: VisibilityConfig;
  onChange: (next: VisibilityConfig) => void;
  // Optional intro text. The CultivarDetail tab supplies its own.
  intro?: string;
}

export default function VisibilityConfigEditor({ value, onChange, intro }: Props) {
  const config = value || {};

  const toggleField = (audience: VisibilityAudience, field: string) => {
    const a = { ...(config[audience] || {}) };
    const fields = new Set(a.fields || []);
    if (fields.has(field)) fields.delete(field); else fields.add(field);
    a.fields = Array.from(fields);
    onChange({ ...config, [audience]: a });
  };
  const toggleCategory = (audience: VisibilityAudience, cat: string) => {
    const a = { ...(config[audience] || {}) };
    const cats = new Set(a.attribute_categories || []);
    if (cats.has(cat)) cats.delete(cat); else cats.add(cat);
    a.attribute_categories = Array.from(cats);
    onChange({ ...config, [audience]: a });
  };

  return (
    <div>
      {intro && <p className="text-xs text-neutral-600 mb-4">{intro}</p>}
      <div className="space-y-4">
        {AUDIENCES.map(a => {
          const cfg = config[a.key] || {};
          const fields = new Set(cfg.fields || []);
          const cats = new Set(cfg.attribute_categories || []);
          return (
            <div key={a.key} className="border border-neutral-200 rounded-md p-3">
              <div className="mb-2">
                <div className="font-medium text-sm">{a.label}</div>
                <div className="text-xs text-neutral-500">{a.hint}</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs font-medium text-neutral-700 mb-1">Additional fields</div>
                  <div className="flex flex-wrap gap-1">
                    {FIELD_OPTIONS.map(f => (
                      <label key={f}
                             className={`text-xs px-2 py-1 rounded cursor-pointer border ${fields.has(f) ? 'bg-primary-50 border-primary-300 text-primary-800' : 'bg-white border-neutral-200 text-neutral-700'}`}>
                        <input type="checkbox" className="hidden" checked={fields.has(f)}
                               onChange={() => toggleField(a.key, f)} />
                        {f}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium text-neutral-700 mb-1">Attribute categories</div>
                  <div className="flex flex-wrap gap-1">
                    {ATTRIBUTE_CATEGORIES.map(c => (
                      <label key={c}
                             className={`text-xs px-2 py-1 rounded cursor-pointer border ${cats.has(c) ? 'bg-primary-50 border-primary-300 text-primary-800' : 'bg-white border-neutral-200 text-neutral-700'}`}>
                        <input type="checkbox" className="hidden" checked={cats.has(c)}
                               onChange={() => toggleCategory(a.key, c)} />
                        {c.replace(/_/g, ' ')}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
